import { chalkLine, chalkText, getChalkRole } from '../../primitives/chalk';
import {
  normalizePedagogicalPattern,
  derivePatternState,
  itemAlphaForFocus,
} from '../../core/patternVocabulary';

const ALLOWED_VARIANTS = new Set([
  'shapes',
  'character_scene',
  'table',
  'chart',
  'graph',
  'chat',
  'timeline',
  'tree',
  'stack_queue',
  'coordinate_plane',
  'flow_diagram',
  'set_diagram',
  'heatmap',
  'field',
]);

function toNumber(value, fallback) {
  return Number.isFinite(value) ? Number(value) : fallback;
}

function clamp01(value) {
  return Math.max(0, Math.min(1, value));
}

function computePoint(area, xk = 0.5, yk = 0.5) {
  return {
    x: area.x + area.w * clamp01(toNumber(xk, 0.5)),
    y: area.y + area.h * clamp01(toNumber(yk, 0.5)),
  };
}

function drawLabelBadge(ctx, text, x, y, size) {
  const safeText = String(text || '');
  if (!safeText) return;

  ctx.save();
  ctx.font = `${size}px sans-serif`;
  ctx.textAlign = 'center';
  const width = Math.max(20, ctx.measureText(safeText).width + 16);
  const height = Math.max(size + 10, 20);
  const rectX = x - width / 2;
  const rectY = y - size * 0.8 - 6;

  ctx.fillStyle = 'rgba(9, 18, 30, 0.86)';
  ctx.strokeStyle = 'rgba(86, 182, 255, 0.72)';
  ctx.lineWidth = 1.2;
  ctx.fillRect(rectX, rectY, width, height);
  ctx.strokeRect(rectX, rectY, width, height);
  ctx.restore();

  chalkText(ctx, safeText, x, y, size, 0.98, 'sans-serif', 'center', { role: 'diagramLabel' });
}

function normalizeShapes(spec) {
  const shapes = Array.isArray(spec.shapes) ? spec.shapes : [];
  return shapes.slice(0, 32).map((shape, idx) => ({
    id: String(shape.id || `shape_${idx + 1}`),
    kind: String(shape.kind || 'rect').toLowerCase(),
    xk: toNumber(shape.xk, 0.5),
    yk: toNumber(shape.yk, 0.5),
    wk: toNumber(shape.wk, 0.2),
    hk: toNumber(shape.hk, 0.14),
    label: shape.label ? String(shape.label) : '',
    stroke: shape.stroke ? String(shape.stroke) : '',
    fill: shape.fill ? String(shape.fill) : '',
  }));
}

function normalizeCharacters(spec) {
  const characters = Array.isArray(spec.characters) ? spec.characters : [];
  return characters.slice(0, 8).map((character, idx) => ({
    id: String(character.id || `char_${idx + 1}`),
    name: String(character.name || `Character ${idx + 1}`),
    xk: toNumber(character.xk, 0.2 + idx * 0.2),
    yk: toNumber(character.yk, 0.72),
    scale: toNumber(character.scale, 1),
    pose: String(character.pose || 'idle').toLowerCase(),
    color: character.color ? String(character.color) : '',
  }));
}

function normalizeTable(spec) {
  const headers = Array.isArray(spec.table?.headers)
    ? spec.table.headers.map((h) => String(h)).slice(0, 8)
    : [];
  const rows = Array.isArray(spec.table?.rows)
    ? spec.table.rows.slice(0, 10).map((row) => (Array.isArray(row) ? row.map(String) : []))
    : [];
  return {
    headers,
    rows,
    caption: spec.table?.caption ? String(spec.table.caption) : '',
  };
}

function normalizeChart(spec) {
  const chartType = String(spec.chart?.type || 'bar').toLowerCase();
  const labels = Array.isArray(spec.chart?.labels)
    ? spec.chart.labels.map(String).slice(0, 10)
    : [];
  const series = Array.isArray(spec.chart?.series)
    ? spec.chart.series.slice(0, 4).map((item, idx) => ({
        name: String(item.name || `Series ${idx + 1}`),
        values: Array.isArray(item.values)
          ? item.values.map((v) => toNumber(v, 0)).slice(0, labels.length || 10)
          : [],
        color: item.color ? String(item.color) : '',
      }))
    : [];
  return { type: chartType === 'line' ? 'line' : 'bar', labels, series };
}

function normalizeGraph(spec) {
  const nodes = Array.isArray(spec.graph?.nodes)
    ? spec.graph.nodes.slice(0, 20).map((node, idx) => ({
        id: String(node.id || `g_${idx + 1}`),
        label: String(node.label || node.id || `Node ${idx + 1}`),
        xk: toNumber(node.xk, 0.15 + (idx % 5) * 0.17),
        yk: toNumber(node.yk, 0.25 + Math.floor(idx / 5) * 0.2),
      }))
    : [];
  const idSet = new Set(nodes.map((node) => node.id));
  const edges = Array.isArray(spec.graph?.edges)
    ? spec.graph.edges
        .slice(0, 30)
        .map((edge) => ({
          from: String(edge.from || ''),
          to: String(edge.to || ''),
          label: edge.label ? String(edge.label) : '',
          directed: edge.directed !== false,
        }))
        .filter((edge) => idSet.has(edge.from) && idSet.has(edge.to))
    : [];
  return { nodes, edges };
}

function normalizeChat(spec) {
  const participants = Array.isArray(spec.chat?.participants)
    ? spec.chat.participants.slice(0, 6).map((p, idx) => ({
        id: String(p.id || `speaker_${idx + 1}`),
        name: String(p.name || `Speaker ${idx + 1}`),
        xk: toNumber(p.xk, idx % 2 === 0 ? 0.2 : 0.8),
      }))
    : [];
  const speakerSet = new Set(participants.map((p) => p.id));
  const messages = Array.isArray(spec.chat?.messages)
    ? spec.chat.messages
        .slice(0, 12)
        .map((m, idx) => ({
          id: String(m.id || `msg_${idx + 1}`),
          speakerId: String(m.speakerId || participants[0]?.id || ''),
          text: String(m.text || ''),
          atSec: toNumber(m.atSec, idx * 0.8),
        }))
        .filter((m) => speakerSet.has(m.speakerId) && m.text)
    : [];
  return { participants, messages };
}

function normalizeTimeline(spec) {
  const events = Array.isArray(spec.timeline?.events)
    ? spec.timeline.events.slice(0, 14).map((event, idx) => ({
        id: String(event.id || `event_${idx + 1}`),
        label: String(event.label || `Event ${idx + 1}`),
        atK: clamp01(toNumber(event.atK, idx / Math.max(1, 13))),
      }))
    : [];
  return { events };
}

function normalizeTree(spec) {
  const nodes = Array.isArray(spec.tree?.nodes)
    ? spec.tree.nodes.slice(0, 20).map((node, idx) => ({
        id: String(node.id || `t_${idx + 1}`),
        label: String(node.label || node.id || `Node ${idx + 1}`),
        xk: toNumber(node.xk, 0.15 + (idx % 5) * 0.17),
        yk: toNumber(node.yk, 0.2 + Math.floor(idx / 5) * 0.18),
      }))
    : [];
  const nodeSet = new Set(nodes.map((node) => node.id));
  const edges = Array.isArray(spec.tree?.edges)
    ? spec.tree.edges
        .slice(0, 30)
        .map((edge) => ({
          from: String(edge.from || ''),
          to: String(edge.to || ''),
          label: edge.label ? String(edge.label) : '',
        }))
        .filter((edge) => nodeSet.has(edge.from) && nodeSet.has(edge.to))
    : [];
  return { nodes, edges };
}

function normalizeStackQueue(spec) {
  const mode =
    String(spec.stackQueue?.mode || 'stack').toLowerCase() === 'queue' ? 'queue' : 'stack';
  const items = Array.isArray(spec.stackQueue?.items)
    ? spec.stackQueue.items.slice(0, 10).map((item) => String(item))
    : [];
  return { mode, items };
}

function normalizeCoordinatePlane(spec) {
  const points = Array.isArray(spec.coordinatePlane?.points)
    ? spec.coordinatePlane.points.slice(0, 16).map((point, idx) => ({
        id: String(point.id || `p_${idx + 1}`),
        label: point.label ? String(point.label) : '',
        xk: clamp01(toNumber(point.xk, 0.2 + idx * 0.04)),
        yk: clamp01(toNumber(point.yk, 0.5)),
      }))
    : [];
  const curves = Array.isArray(spec.coordinatePlane?.curves)
    ? spec.coordinatePlane.curves.slice(0, 4).map((curve, idx) => ({
        id: String(curve.id || `curve_${idx + 1}`),
        points: Array.isArray(curve.points)
          ? curve.points.slice(0, 24).map((point) => ({
              xk: clamp01(toNumber(point.xk, 0.5)),
              yk: clamp01(toNumber(point.yk, 0.5)),
            }))
          : [],
      }))
    : [];
  return { points, curves };
}

function normalizeFlowDiagram(spec) {
  const stages = Array.isArray(spec.flowDiagram?.stages)
    ? spec.flowDiagram.stages.slice(0, 12).map((stage, idx) => ({
        id: String(stage.id || `stage_${idx + 1}`),
        label: String(stage.label || `Stage ${idx + 1}`),
        xk: toNumber(stage.xk, 0.14 + (idx % 4) * 0.22),
        yk: toNumber(stage.yk, 0.35 + Math.floor(idx / 4) * 0.24),
      }))
    : [];
  const stageSet = new Set(stages.map((stage) => stage.id));
  const links = Array.isArray(spec.flowDiagram?.links)
    ? spec.flowDiagram.links
        .slice(0, 20)
        .map((link) => ({
          from: String(link.from || ''),
          to: String(link.to || ''),
          label: link.label ? String(link.label) : '',
        }))
        .filter((link) => stageSet.has(link.from) && stageSet.has(link.to))
    : [];
  return { stages, links };
}

function normalizeSetDiagram(spec) {
  const sets = Array.isArray(spec.setDiagram?.sets)
    ? spec.setDiagram.sets.slice(0, 4).map((setItem, idx) => ({
        id: String(setItem.id || `set_${idx + 1}`),
        label: String(setItem.label || `Set ${idx + 1}`),
        xk: toNumber(setItem.xk, idx === 0 ? 0.42 : 0.58),
        yk: toNumber(setItem.yk, 0.52),
        rk: clamp01(toNumber(setItem.rk, 0.22)),
      }))
    : [];
  return { sets };
}

function normalizeHeatmap(spec) {
  const labelsX = Array.isArray(spec.heatmap?.labelsX)
    ? spec.heatmap.labelsX.slice(0, 10).map((label) => String(label))
    : [];
  const labelsY = Array.isArray(spec.heatmap?.labelsY)
    ? spec.heatmap.labelsY.slice(0, 10).map((label) => String(label))
    : [];
  const values = Array.isArray(spec.heatmap?.values)
    ? spec.heatmap.values
        .slice(0, 10)
        .map((row) => (Array.isArray(row) ? row.slice(0, 10).map((v) => toNumber(v, 0)) : []))
    : [];
  return { labelsX, labelsY, values };
}

function normalizeField(spec) {
  const vectors = Array.isArray(spec.field?.vectors)
    ? spec.field.vectors.slice(0, 24).map((vector, idx) => ({
        id: String(vector.id || `vec_${idx + 1}`),
        xk: clamp01(toNumber(vector.xk, 0.2 + (idx % 6) * 0.12)),
        yk: clamp01(toNumber(vector.yk, 0.2 + Math.floor(idx / 6) * 0.15)),
        dxk: toNumber(vector.dxk, 0.05),
        dyk: toNumber(vector.dyk, -0.04),
      }))
    : [];
  return { vectors };
}

function drawShape(ctx, shape, area, progress) {
  const center = computePoint(area, shape.xk, shape.yk);
  const w = area.w * clamp01(shape.wk);
  const h = area.h * clamp01(shape.hk);
  const halfW = w / 2;
  const halfH = h / 2;

  ctx.save();
  ctx.strokeStyle = shape.stroke || getChalkRole('diagramNodeStroke').color;
  ctx.fillStyle = shape.fill || 'rgba(255, 183, 77, 0.12)';
  ctx.lineWidth = 2;

  const kind = shape.kind;
  if (kind === 'oval' || kind === 'ellipse') {
    ctx.beginPath();
    ctx.ellipse(center.x, center.y, halfW * progress, halfH * progress, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  } else if (kind === 'triangle') {
    const yTop = center.y - halfH;
    const yBottom = center.y + halfH;
    const xLeft = center.x - halfW;
    const xRight = center.x + halfW;
    const xMid = center.x;
    ctx.beginPath();
    ctx.moveTo(xMid, yTop);
    ctx.lineTo(xRight, yBottom);
    ctx.lineTo(xLeft, yBottom);
    ctx.closePath();
    ctx.globalAlpha = progress;
    ctx.fill();
    ctx.stroke();
  } else if (kind === 'diamond') {
    ctx.beginPath();
    ctx.moveTo(center.x, center.y - halfH);
    ctx.lineTo(center.x + halfW, center.y);
    ctx.lineTo(center.x, center.y + halfH);
    ctx.lineTo(center.x - halfW, center.y);
    ctx.closePath();
    ctx.globalAlpha = progress;
    ctx.fill();
    ctx.stroke();
  } else {
    // default rectangle/square
    ctx.globalAlpha = progress;
    ctx.fillRect(center.x - halfW, center.y - halfH, w, h);
    ctx.strokeRect(center.x - halfW, center.y - halfH, w, h);
  }
  ctx.restore();

  if (progress >= 1 && shape.label) {
    drawLabelBadge(ctx, shape.label, center.x, center.y + halfH + 18, Math.max(12, area.h * 0.028));
  }
}

function drawStickCharacter(ctx, character, area, progress) {
  const base = computePoint(area, character.xk, character.yk);
  const scale = Math.max(0.6, toNumber(character.scale, 1));
  const headR = 10 * scale;
  const bodyH = 32 * scale;
  const armSpan = 20 * scale;
  const legSpan = 14 * scale;
  const pose = character.pose;
  const wave = Math.sin(progress * Math.PI * 2) * 4 * scale;

  ctx.save();
  ctx.strokeStyle = character.color || getChalkRole('diagramNodeStroke').color;
  ctx.lineWidth = 2.2;
  ctx.globalAlpha = 0.96;

  // head
  ctx.beginPath();
  ctx.arc(base.x, base.y - bodyH - headR, headR, 0, Math.PI * 2);
  ctx.stroke();

  // torso
  chalkLine(ctx, base.x, base.y - bodyH, base.x, base.y - 6, 2.2, 0.94, {
    role: 'diagramNodeStroke',
  });

  // arms
  const armY = base.y - bodyH + 10;
  chalkLine(ctx, base.x, armY, base.x - armSpan, armY + (pose === 'point' ? -5 : 5), 2, 0.9, {
    role: 'diagramNodeStroke',
  });
  chalkLine(
    ctx,
    base.x,
    armY,
    base.x + armSpan,
    armY + (pose === 'wave' ? wave : pose === 'point' ? -8 : 5),
    2,
    0.9,
    { role: 'diagramNodeStroke' }
  );

  // legs
  chalkLine(ctx, base.x, base.y - 6, base.x - legSpan, base.y + 18, 2, 0.9, {
    role: 'diagramNodeStroke',
  });
  chalkLine(ctx, base.x, base.y - 6, base.x + legSpan, base.y + 18, 2, 0.9, {
    role: 'diagramNodeStroke',
  });
  ctx.restore();

  drawLabelBadge(ctx, character.name, base.x, base.y + 34, Math.max(11, area.h * 0.022));
}

export async function preparePedagogicalDiagramNode(env, spec = {}) {
  const area = env.boardArea();
  const variantRaw = String(spec.variant || spec.type || 'shapes').toLowerCase();
  const variant = ALLOWED_VARIANTS.has(variantRaw) ? variantRaw : 'shapes';

  return {
    area,
    variant,
    pattern: normalizePedagogicalPattern(spec.pattern),
    title: spec.title ? String(spec.title) : '',
    subtitle: spec.subtitle ? String(spec.subtitle) : '',
    titleSize: env.getStyle('bodySize'),
    labelSize: env.getStyle('smallSize'),
    durationSec: Math.max(2, toNumber(spec.durationSec, 6)),
    shapes: normalizeShapes(spec),
    characters: normalizeCharacters(spec),
    table: normalizeTable(spec),
    chart: normalizeChart(spec),
    graph: normalizeGraph(spec),
    chat: normalizeChat(spec),
    timeline: normalizeTimeline(spec),
    tree: normalizeTree(spec),
    stackQueue: normalizeStackQueue(spec),
    coordinatePlane: normalizeCoordinatePlane(spec),
    flowDiagram: normalizeFlowDiagram(spec),
    setDiagram: normalizeSetDiagram(spec),
    heatmap: normalizeHeatmap(spec),
    field: normalizeField(spec),
  };
}

export function commitPedagogicalDiagramNode(packet, ctxs, anim) {
  let t = 0;

  anim.add((dt) => {
    t += dt;
    const baseProgress = clamp01(t / Math.max(0.001, packet.durationSec));
    const pattern = normalizePedagogicalPattern(packet.pattern);
    const progress =
      pattern === 'PROGRESSIVE_REVEAL' || pattern === 'BUILD_UP_CONSTRUCTION'
        ? clamp01(baseProgress * 0.95)
        : baseProgress;
    const patternState = derivePatternState({
      pattern,
      progress,
      timeSec: t,
      durationSec: packet.durationSec,
      totalItems:
        packet.variant === 'table'
          ? packet.table.rows.length + (packet.table.headers.length > 0 ? 1 : 0)
          : packet.variant === 'chart'
            ? packet.chart.labels.length
            : packet.variant === 'graph'
              ? packet.graph.nodes.length + packet.graph.edges.length
              : packet.variant === 'timeline'
                ? packet.timeline.events.length
                : packet.variant === 'tree'
                  ? packet.tree.nodes.length + packet.tree.edges.length
                  : packet.variant === 'flow_diagram'
                    ? packet.flowDiagram.stages.length + packet.flowDiagram.links.length
                    : 10,
    });
    const ctx = ctxs.ink;
    ctx.clearRect(packet.area.x, packet.area.y, packet.area.w, packet.area.h);

    if (packet.title) {
      chalkText(
        ctx,
        packet.title,
        packet.area.x + packet.area.w / 2,
        packet.area.y + 26,
        packet.titleSize,
        0.95,
        'sans-serif',
        'center',
        { role: 'diagramTitle' }
      );
    }
    if (packet.subtitle) {
      chalkText(
        ctx,
        packet.subtitle,
        packet.area.x + packet.area.w / 2,
        packet.area.y + packet.titleSize + 34,
        packet.labelSize,
        0.8,
        'sans-serif',
        'center',
        { role: 'diagramSubtitle' }
      );
    }

    if (packet.variant === 'shapes') {
      const total = Math.max(1, packet.shapes.length);
      packet.shapes.forEach((shape, index) => {
        const local = clamp01(progress * total - index);
        if (local > 0) drawShape(ctx, shape, packet.area, local);
      });
    } else if (packet.variant === 'character_scene') {
      const groundY = packet.area.y + packet.area.h * 0.82;
      chalkLine(
        ctx,
        packet.area.x + 20,
        groundY,
        packet.area.x + packet.area.w - 20,
        groundY,
        2,
        0.6,
        { role: 'diagramEdge' }
      );
      const total = Math.max(1, packet.characters.length);
      packet.characters.forEach((character, index) => {
        const local = clamp01(progress * total - index);
        if (local > 0) drawStickCharacter(ctx, character, packet.area, local);
      });
    } else if (packet.variant === 'table') {
      const headers = packet.table.headers;
      const rows = packet.table.rows;
      const cols = Math.max(1, headers.length || rows[0]?.length || 1);
      const totalRows = rows.length + (headers.length > 0 ? 1 : 0);
      const cellW = (packet.area.w * 0.9) / cols;
      const cellH = Math.min(56, (packet.area.h * 0.68) / Math.max(1, totalRows));
      const x0 = packet.area.x + packet.area.w * 0.05;
      const y0 = packet.area.y + packet.area.h * 0.2;
      const visibleRows = Math.min(totalRows, patternState.visibleCount);

      for (let r = 0; r < visibleRows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = x0 + c * cellW;
          const y = y0 + r * cellH;
          ctx.save();
          ctx.fillStyle =
            r === 0 && headers.length ? 'rgba(255,165,54,0.18)' : 'rgba(255,255,255,0.06)';
          ctx.strokeStyle = 'rgba(109, 242, 255, 0.65)';
          ctx.lineWidth = 1.2;
          ctx.fillRect(x, y, cellW, cellH);
          ctx.strokeRect(x, y, cellW, cellH);
          ctx.restore();

          const value =
            r === 0 && headers.length
              ? headers[c] || ''
              : rows[r - (headers.length ? 1 : 0)]?.[c] || '';
          if (value) {
            chalkText(
              ctx,
              value,
              x + cellW / 2,
              y + cellH * 0.62,
              Math.min(packet.labelSize, 15),
              0.94,
              'sans-serif',
              'center',
              { role: 'diagramLabel' }
            );
          }
        }
      }
      if (packet.table.caption) {
        chalkText(
          ctx,
          packet.table.caption,
          packet.area.x + packet.area.w / 2,
          y0 + visibleRows * cellH + 22,
          packet.labelSize,
          0.8,
          'sans-serif',
          'center',
          { role: 'diagramSubtitle' }
        );
      }
    } else if (packet.variant === 'chart') {
      const x0 = packet.area.x + packet.area.w * 0.12;
      const y0 = packet.area.y + packet.area.h * 0.82;
      const chartW = packet.area.w * 0.78;
      const chartH = packet.area.h * 0.52;
      const labels = packet.chart.labels;
      const series = packet.chart.series;
      const values = series.flatMap((s) => s.values);
      const maxV = Math.max(1, ...values);
      chalkLine(ctx, x0, y0, x0, y0 - chartH, 2, 0.7, { role: 'diagramEdge' });
      chalkLine(ctx, x0, y0, x0 + chartW, y0, 2, 0.7, { role: 'diagramEdge' });

      if (packet.chart.type === 'line') {
        const first = series[0];
        if (first && first.values.length > 1) {
          const n = first.values.length;
          const visible = Math.max(1, Math.floor(n * progress));
          for (let i = 1; i < visible; i++) {
            const xA = x0 + ((i - 1) / Math.max(1, n - 1)) * chartW;
            const yA = y0 - (first.values[i - 1] / maxV) * chartH;
            const xB = x0 + (i / Math.max(1, n - 1)) * chartW;
            const yB = y0 - (first.values[i] / maxV) * chartH;
            chalkLine(ctx, xA, yA, xB, yB, 2.2, 0.9, { role: 'diagramNodeStroke' });
          }
          for (let i = 0; i < visible; i++) {
            const x = x0 + (i / Math.max(1, n - 1)) * chartW;
            const y = y0 - (first.values[i] / maxV) * chartH;
            ctx.save();
            ctx.fillStyle = 'rgba(255,183,77,0.95)';
            ctx.beginPath();
            ctx.arc(x, y, 3.5, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
            if (labels[i]) {
              chalkText(ctx, labels[i], x, y0 + 16, 11, 0.82, 'sans-serif', 'center', {
                role: 'diagramSubtitle',
              });
            }
          }
        }
      } else {
        const first = series[0];
        if (first) {
          const n = Math.max(1, first.values.length);
          const barW = (chartW / n) * 0.62;
          const visibleBars = Math.max(1, Math.ceil(n * progress));
          for (let i = 0; i < visibleBars; i++) {
            const value = first.values[i] || 0;
            const h = (value / maxV) * chartH;
            const x = x0 + (i + 0.5) * (chartW / n) - barW / 2;
            const y = y0 - h;
            ctx.save();
            ctx.fillStyle = first.color || 'rgba(255, 165, 54, 0.7)';
            ctx.strokeStyle = 'rgba(255, 214, 145, 0.95)';
            ctx.lineWidth = 1.2;
            ctx.fillRect(x, y, barW, h);
            ctx.strokeRect(x, y, barW, h);
            ctx.restore();
            if (labels[i]) {
              chalkText(ctx, labels[i], x + barW / 2, y0 + 16, 11, 0.82, 'sans-serif', 'center', {
                role: 'diagramSubtitle',
              });
            }
          }
        }
      }
    } else if (packet.variant === 'graph') {
      const nodes = packet.graph.nodes.map((node) => ({
        ...node,
        ...computePoint(packet.area, node.xk, node.yk),
      }));
      const byId = new Map(nodes.map((n) => [n.id, n]));
      const total = Math.max(1, packet.graph.edges.length + nodes.length);
      const visibleCount = Math.min(total, patternState.visibleCount);
      const visibleEdges = Math.min(packet.graph.edges.length, visibleCount);
      const visibleNodes = Math.max(0, visibleCount - packet.graph.edges.length);

      for (let i = 0; i < visibleEdges; i++) {
        const edge = packet.graph.edges[i];
        const from = byId.get(edge.from);
        const to = byId.get(edge.to);
        if (!from || !to) continue;
        chalkLine(ctx, from.x, from.y, to.x, to.y, 2, 0.82, { role: 'diagramEdge' });
        if (edge.label) {
          drawLabelBadge(ctx, edge.label, (from.x + to.x) / 2, (from.y + to.y) / 2 - 8, 11);
        }
      }
      for (let i = 0; i < Math.min(nodes.length, visibleNodes); i++) {
        const node = nodes[i];
        ctx.save();
        ctx.globalAlpha = itemAlphaForFocus(patternState, i);
        ctx.fillStyle = 'rgba(255, 183, 77, 0.18)';
        ctx.strokeStyle = getChalkRole('diagramNodeStroke').color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(node.x, node.y, 16, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
        drawLabelBadge(ctx, node.label, node.x, node.y + 30, 12);
      }
    } else if (packet.variant === 'chat') {
      const participants = packet.chat.participants;
      const byId = new Map(participants.map((p) => [p.id, p]));
      participants.forEach((participant) => {
        const top = computePoint(packet.area, participant.xk, 0.14);
        drawLabelBadge(ctx, participant.name, top.x, top.y, Math.max(12, packet.labelSize));
      });

      const elapsed = progress * packet.durationSec;
      const visibleMessages = packet.chat.messages.filter((m) => elapsed >= m.atSec);
      visibleMessages.forEach((message, idx) => {
        const speaker = byId.get(message.speakerId);
        if (!speaker) return;
        const left = speaker.xk <= 0.5;
        const bubbleW = packet.area.w * 0.42;
        const bubbleH = 48;
        const x = packet.area.x + (left ? packet.area.w * 0.05 : packet.area.w * 0.53);
        const y = packet.area.y + packet.area.h * 0.24 + idx * (bubbleH + 10);
        ctx.save();
        ctx.fillStyle = left ? 'rgba(255,165,54,0.2)' : 'rgba(109,242,255,0.18)';
        ctx.strokeStyle = left ? 'rgba(255,190,120,0.9)' : 'rgba(109,242,255,0.9)';
        ctx.lineWidth = 1.2;
        ctx.fillRect(x, y, bubbleW, bubbleH);
        ctx.strokeRect(x, y, bubbleW, bubbleH);
        ctx.restore();
        chalkText(
          ctx,
          message.text.length > 64 ? `${message.text.slice(0, 61)}...` : message.text,
          x + 10,
          y + bubbleH * 0.62,
          Math.min(packet.labelSize, 13),
          0.95,
          'sans-serif',
          'left',
          { role: 'diagramLabel' }
        );
      });
    } else if (packet.variant === 'timeline') {
      const axisY = packet.area.y + packet.area.h * 0.58;
      const x0 = packet.area.x + packet.area.w * 0.1;
      const x1 = packet.area.x + packet.area.w * 0.9;
      chalkLine(ctx, x0, axisY, x1, axisY, 2, 0.82, { role: 'diagramEdge' });
      const events = packet.timeline.events;
      const visible = Math.min(events.length, patternState.visibleCount);
      for (let i = 0; i < visible; i++) {
        const event = events[i];
        const x = x0 + (x1 - x0) * clamp01(event.atK);
        ctx.save();
        ctx.fillStyle = 'rgba(255,183,77,0.85)';
        ctx.beginPath();
        ctx.arc(x, axisY, 4.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        chalkLine(ctx, x, axisY, x, axisY - 24, 1.5, 0.7, { role: 'diagramEdge' });
        drawLabelBadge(ctx, event.label, x, axisY - 30, 11);
      }
    } else if (packet.variant === 'tree') {
      const nodes = packet.tree.nodes.map((node) => ({
        ...node,
        ...computePoint(packet.area, node.xk, node.yk),
      }));
      const byId = new Map(nodes.map((node) => [node.id, node]));
      const visibleEdgeCount = Math.min(packet.tree.edges.length, patternState.visibleCount);
      const visibleNodeCount = Math.min(
        nodes.length,
        Math.max(0, patternState.visibleCount - packet.tree.edges.length)
      );
      for (let i = 0; i < visibleEdgeCount; i++) {
        const edge = packet.tree.edges[i];
        const from = byId.get(edge.from);
        const to = byId.get(edge.to);
        if (!from || !to) continue;
        chalkLine(ctx, from.x, from.y, to.x, to.y, 1.8, 0.8, { role: 'diagramEdge' });
      }
      for (let i = 0; i < visibleNodeCount; i++) {
        const node = nodes[i];
        ctx.save();
        ctx.globalAlpha = itemAlphaForFocus(patternState, i);
        ctx.fillStyle = 'rgba(86,182,255,0.16)';
        ctx.strokeStyle = getChalkRole('diagramNodeStroke').color;
        ctx.lineWidth = 1.8;
        ctx.beginPath();
        ctx.arc(node.x, node.y, 14, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
        drawLabelBadge(ctx, node.label, node.x, node.y + 28, 11);
      }
    } else if (packet.variant === 'stack_queue') {
      const mode = packet.stackQueue.mode;
      const items = packet.stackQueue.items;
      const maxItems = Math.max(1, items.length);
      const visible = Math.min(maxItems, patternState.visibleCount);
      const boxW = packet.area.w * 0.44;
      const boxH = Math.min(44, (packet.area.h * 0.6) / Math.max(1, maxItems));
      const baseX = packet.area.x + (packet.area.w - boxW) / 2;
      const baseY = packet.area.y + packet.area.h * 0.24;
      for (let i = 0; i < visible; i++) {
        const index = mode === 'queue' ? i : visible - i - 1;
        const y = baseY + i * boxH;
        ctx.save();
        ctx.fillStyle = 'rgba(255,165,54,0.16)';
        ctx.strokeStyle = 'rgba(109,242,255,0.8)';
        ctx.lineWidth = 1.2;
        ctx.fillRect(baseX, y, boxW, boxH - 2);
        ctx.strokeRect(baseX, y, boxW, boxH - 2);
        ctx.restore();
        if (items[index]) {
          chalkText(
            ctx,
            items[index],
            baseX + 10,
            y + boxH * 0.62,
            12,
            0.95,
            'sans-serif',
            'left',
            { role: 'diagramLabel' }
          );
        }
      }
      drawLabelBadge(
        ctx,
        mode === 'queue' ? 'Queue: FIFO' : 'Stack: LIFO',
        packet.area.x + packet.area.w / 2,
        baseY + visible * boxH + 18,
        11
      );
    } else if (packet.variant === 'coordinate_plane') {
      const x0 = packet.area.x + packet.area.w * 0.14;
      const y0 = packet.area.y + packet.area.h * 0.78;
      const w = packet.area.w * 0.74;
      const h = packet.area.h * 0.56;
      chalkLine(ctx, x0, y0, x0 + w, y0, 1.8, 0.75, { role: 'diagramEdge' });
      chalkLine(ctx, x0, y0, x0, y0 - h, 1.8, 0.75, { role: 'diagramEdge' });
      const visiblePointCount = Math.min(
        packet.coordinatePlane.points.length,
        patternState.visibleCount
      );
      for (let i = 0; i < visiblePointCount; i++) {
        const point = packet.coordinatePlane.points[i];
        const x = x0 + w * clamp01(point.xk);
        const y = y0 - h * clamp01(point.yk);
        ctx.save();
        ctx.fillStyle = 'rgba(255,183,77,0.9)';
        ctx.beginPath();
        ctx.arc(x, y, 3.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        if (point.label) drawLabelBadge(ctx, point.label, x, y - 6, 10);
      }
      packet.coordinatePlane.curves.forEach((curve, idx) => {
        const points = Array.isArray(curve.points) ? curve.points : [];
        if (points.length < 2) return;
        const visible = Math.max(2, Math.ceil(points.length * progress));
        for (let i = 1; i < visible; i++) {
          const a = points[i - 1];
          const b = points[i];
          chalkLine(
            ctx,
            x0 + w * clamp01(a.xk),
            y0 - h * clamp01(a.yk),
            x0 + w * clamp01(b.xk),
            y0 - h * clamp01(b.yk),
            idx === 0 ? 2.2 : 1.5,
            0.84,
            { role: 'diagramNodeStroke' }
          );
        }
      });
    } else if (packet.variant === 'flow_diagram') {
      const stages = packet.flowDiagram.stages.map((stage) => ({
        ...stage,
        ...computePoint(packet.area, stage.xk, stage.yk),
      }));
      const byId = new Map(stages.map((stage) => [stage.id, stage]));
      const visibleLinks = Math.min(packet.flowDiagram.links.length, patternState.visibleCount);
      const visibleStages = Math.min(
        stages.length,
        Math.max(0, patternState.visibleCount - packet.flowDiagram.links.length)
      );
      for (let i = 0; i < visibleLinks; i++) {
        const link = packet.flowDiagram.links[i];
        const from = byId.get(link.from);
        const to = byId.get(link.to);
        if (!from || !to) continue;
        chalkLine(ctx, from.x, from.y, to.x, to.y, 1.8, 0.8, { role: 'diagramEdge' });
        if (link.label)
          drawLabelBadge(ctx, link.label, (from.x + to.x) / 2, (from.y + to.y) / 2 - 8, 10);
      }
      for (let i = 0; i < visibleStages; i++) {
        const stage = stages[i];
        const w = 90;
        const h = 36;
        ctx.save();
        ctx.globalAlpha = itemAlphaForFocus(patternState, i);
        ctx.fillStyle = 'rgba(255,165,54,0.16)';
        ctx.strokeStyle = 'rgba(109,242,255,0.86)';
        ctx.lineWidth = 1.2;
        ctx.fillRect(stage.x - w / 2, stage.y - h / 2, w, h);
        ctx.strokeRect(stage.x - w / 2, stage.y - h / 2, w, h);
        ctx.restore();
        chalkText(ctx, stage.label, stage.x, stage.y + 6, 11, 0.94, 'sans-serif', 'center', {
          role: 'diagramLabel',
        });
      }
    } else if (packet.variant === 'set_diagram') {
      const sets = packet.setDiagram.sets;
      const visible = Math.ceil(sets.length * progress);
      for (let i = 0; i < visible; i++) {
        const setItem = sets[i];
        const center = computePoint(packet.area, setItem.xk, setItem.yk);
        const radius = packet.area.w * setItem.rk;
        ctx.save();
        ctx.fillStyle = i % 2 === 0 ? 'rgba(255,165,54,0.14)' : 'rgba(109,242,255,0.14)';
        ctx.strokeStyle = i % 2 === 0 ? 'rgba(255,190,120,0.9)' : 'rgba(109,242,255,0.9)';
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
        drawLabelBadge(ctx, setItem.label, center.x, center.y - radius - 8, 11);
      }
    } else if (packet.variant === 'heatmap') {
      const values = packet.heatmap.values;
      const rows = values.length;
      const cols = Math.max(1, ...values.map((row) => row.length));
      const x0 = packet.area.x + packet.area.w * 0.14;
      const y0 = packet.area.y + packet.area.h * 0.24;
      const w = packet.area.w * 0.72;
      const h = packet.area.h * 0.6;
      const cellW = w / Math.max(1, cols);
      const cellH = h / Math.max(1, rows);
      const maxV = Math.max(1, ...values.flat().map((v) => Math.abs(v)));
      const visibleRows = Math.min(rows, patternState.visibleCount);
      for (let r = 0; r < visibleRows; r++) {
        const row = values[r] || [];
        for (let c = 0; c < cols; c++) {
          const value = Math.abs(toNumber(row[c], 0));
          const alpha = clamp01(value / maxV);
          const x = x0 + c * cellW;
          const y = y0 + r * cellH;
          ctx.save();
          ctx.fillStyle = `rgba(255,165,54,${0.1 + alpha * 0.7})`;
          ctx.strokeStyle = 'rgba(109,242,255,0.4)';
          ctx.lineWidth = 1;
          ctx.fillRect(x, y, cellW, cellH);
          ctx.strokeRect(x, y, cellW, cellH);
          ctx.restore();
        }
      }
    } else if (packet.variant === 'field') {
      const vectors = packet.field.vectors;
      const visible = Math.min(vectors.length, patternState.visibleCount);
      for (let i = 0; i < visible; i++) {
        const vector = vectors[i];
        const p = computePoint(packet.area, vector.xk, vector.yk);
        const q = computePoint(packet.area, vector.xk + vector.dxk, vector.yk + vector.dyk);
        chalkLine(ctx, p.x, p.y, q.x, q.y, 1.6, 0.82, { role: 'diagramEdge' });
        const angle = Math.atan2(q.y - p.y, q.x - p.x);
        const size = 6;
        chalkLine(
          ctx,
          q.x,
          q.y,
          q.x - size * Math.cos(angle - Math.PI / 6),
          q.y - size * Math.sin(angle - Math.PI / 6),
          1.2,
          0.82,
          { role: 'diagramEdge' }
        );
        chalkLine(
          ctx,
          q.x,
          q.y,
          q.x - size * Math.cos(angle + Math.PI / 6),
          q.y - size * Math.sin(angle + Math.PI / 6),
          1.2,
          0.82,
          { role: 'diagramEdge' }
        );
      }
    }

    return t < packet.durationSec;
  });
}
